import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from './AuthContext';
import { panelKeyForPath } from '../config/accessPanels';

type PanelLevel = 'view' | 'manage';
interface PanelPermission {
  visible: boolean;
  level: PanelLevel;
}

interface AccessClaim {
  hasAccess: boolean;
  permissions: Record<string, PanelPermission>;
}

interface AccessContextValue extends AccessClaim {
  loading: boolean;
  isPanelVisible: (key: string) => boolean;
  isPathVisible: (pathname: string) => boolean;
  canManage: (key: string) => boolean;
  refresh: () => void;
}

// Read the Access profile straight from the JWT (TeamLio embeds it as the
// `access` claim at login; messenger logs in via TeamLio's /api/auth/login-select
// so the token carries it). No API call. Absent/invalid claim → permissive.
function readAccessFromToken(): AccessClaim {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) return { hasAccess: false, permissions: {} };
    const decoded: any = jwtDecode(token);
    const a = decoded?.access;
    if (a && a.has_access) {
      return {
        hasAccess: true,
        permissions: (a.permissions as Record<string, PanelPermission>) || {},
      };
    }
  } catch {
    /* malformed token → permissive */
  }
  return { hasAccess: false, permissions: {} };
}

const DEFAULT: AccessContextValue = {
  loading: false,
  hasAccess: false,
  permissions: {},
  isPanelVisible: () => true,
  isPathVisible: () => true,
  canManage: () => true,
  refresh: () => {},
};

const AccessContext = createContext<AccessContextValue>(DEFAULT);

export const useAccess = () => useContext(AccessContext);

// Expenses was introduced after some Access profiles had already been saved.
// Treat an omitted expense key as the legacy default (visible), but honor an
// explicit Access Management choice as soon as the key is present in the JWT.
export function isPanelVisibleForAccessClaim(
  hasAccess: boolean,
  permissions: Record<string, PanelPermission>,
  key: string,
): boolean {
  if (!hasAccess) return true;
  const permission = permissions[key];
  if (permission === undefined) return key === 'expenses';
  return permission.visible === true;
}

export const AccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [claim, setClaim] = useState<AccessClaim>(() => readAccessFromToken());

  const refresh = useCallback(() => setClaim(readAccessFromToken()), []);

  useEffect(() => {
    refresh();
  }, [isAuthenticated, refresh]);

  const value = useMemo<AccessContextValue>(() => {
    const { hasAccess, permissions } = claim;
    const isPanelVisible = (key: string) =>
      isPanelVisibleForAccessClaim(hasAccess, permissions, key);
    const isPathVisible = (pathname: string) => {
      const key = panelKeyForPath(pathname);
      if (!key) return true; // native page (chat/calls/calendar)
      return isPanelVisible(key);
    };
    const canManage = (key: string) => {
      if (!hasAccess) return true;
      const permission = permissions[key];
      return permission?.visible === true && permission.level === 'manage';
    };
    return { loading: false, ...claim, isPanelVisible, isPathVisible, canManage, refresh };
  }, [claim, refresh]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};
